import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MapPin } from "lucide-react";
import { parseLocation } from "../lib/location";
import { api } from "../lib/api";
import { toast } from "sonner";
import WarehouseReturnsQueue from "./WarehouseReturnsQueue";
import OnsiteReviewPanel from "./OnsiteReviewPanel";
import CompanyMaterialStatus from "./CompanyMaterialStatus";

const Section = ({ title, hint, testId, children }) => (
  <section className="rounded-lg border border-border bg-card" data-testid={testId}>
    <header className="px-4 py-2.5 border-b border-border">
      <h2 className="text-base md:text-lg font-semibold text-foreground">{title}</h2>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </header>
    <div className="p-3">{children}</div>
  </section>
);

/** Manual receipt — the OPS-5 PO receipt stays in Operations; this is the dock's "it showed up" entry with a bin. */
function ReceiveForm({ actor }) {
  const [projects, setProjects] = useState([]);
  const [f, setF] = useState({ project_id: "", product_short: "", qty: "", bin: "", note: "" });
  const [busy, setBusy] = useState(false);
  const loc = parseLocation(f.bin);
  useEffect(() => { api.get("/projects").then(({ data }) => setProjects((Array.isArray(data) ? data : data?.items || []).filter((p) => p.field_active !== false))).catch(() => {}); }, []);
  const save = async () => {
    if (!f.project_id || !f.product_short.trim() || !(Number(f.qty) > 0)) { toast.error("Job, product and a quantity are required"); return; }
    setBusy(true);
    try {
      const uid = `p_${f.product_short.trim().toLowerCase().replace(/[^a-z0-9]+/g, "")}`;
      await api.post("/on-hand/receive", { project_id: f.project_id, product_uid: uid, product_short: f.product_short.trim(), qty: Number(f.qty), bin: loc.ok ? loc.code : "", note: f.note.trim(), actor });
      toast.success(`Received ${f.qty} × ${f.product_short}${loc.ok ? ` at ${loc.code}` : ""}`);
      setF((c) => ({ ...c, product_short: "", qty: "", note: "" }));
    } catch (e) { toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Receipt failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="grid grid-cols-2 md:grid-cols-6 gap-2 items-end" data-testid="receive-form">
      <label className="col-span-2 text-xs text-muted-foreground">Job
        <select className="mt-1 w-full h-10 rounded-md border border-border bg-background px-2 text-sm text-foreground" value={f.project_id} onChange={(e) => setF({ ...f, project_id: e.target.value })} data-testid="receive-project">
          <option value="">Pick a job…</option>
          {projects.map((p) => <option key={p.id} value={p.id}>{p.job_number ? `${p.job_number} · ` : ""}{p.project_name || p.name}</option>)}
        </select>
      </label>
      <label className="col-span-2 md:col-span-1 text-xs text-muted-foreground">Product<Input className="mt-1 h-10" value={f.product_short} onChange={(e) => setF({ ...f, product_short: e.target.value })} placeholder="Part / short" data-testid="receive-product" /></label>
      <label className="text-xs text-muted-foreground">Qty<Input className="mt-1 h-10" type="number" inputMode="decimal" value={f.qty} onChange={(e) => setF({ ...f, qty: e.target.value })} data-testid="receive-qty" /></label>
      <label className="text-xs text-muted-foreground">Location<Input className="mt-1 h-10 font-mono" value={f.bin} onChange={(e) => setF({ ...f, bin: e.target.value })} placeholder="1122" inputMode="numeric" data-testid="receive-bin" /></label>
      <Button className="h-10" disabled={busy || (f.bin.trim() !== "" && !loc.ok)} onClick={save} data-testid="receive-save">Receive</Button>
      {f.bin.trim() !== "" && (
        <div className={`col-span-2 md:col-span-6 inline-flex items-center gap-1.5 text-xs ${loc.ok ? "text-emerald-700 dark:text-emerald-300" : "text-red-700 dark:text-red-300"}`} data-testid="receive-location-confirm">
          <MapPin className="h-3.5 w-3.5" />{loc.ok ? <>Aisle <b>{loc.aisle}</b> · Bay <b>{loc.bay}</b> · Level <b>{loc.level}</b> → stored as <b className="font-mono">{loc.code}</b></> : loc.error}
        </div>
      )}
    </div>
  );
}

/** WH-SIMPLE-1 §4 — Receiving: Receive · On the dock · Coming back. Material moving toward the shelf, top to bottom. */
export default function ReceivingTab({ actor, installers, canPull }) {
  const [tick, setTick] = useState(0);
  return (
    <div className="space-y-4" data-testid="receiving-tab">
      <Section title="Receive" hint="PO and manual receipts land on the shelf with a location — type 1122 for Aisle 11 · Bay 2 · Level 2. Arriving / overdue purchase orders below." testId="receiving-receive">
        <ReceiveForm actor={actor} />
        <div className="mt-3"><CompanyMaterialStatus key={`a${tick}`} sections={["arrivals"]} onChanged={() => setTick((n) => n + 1)} /></div>
      </Section>
      <Section title="On the dock" hint="Returns to check in, unlocated material found or lost, and the surplus sweep." testId="receiving-dock">
        <WarehouseReturnsQueue actor={actor} canPull={canPull} onChanged={() => setTick((n) => n + 1)} />
        <div className="mt-3"><CompanyMaterialStatus key={`s${tick}`} sections={["surplus"]} kpis={false} onChanged={() => setTick((n) => n + 1)} /></div>
      </Section>
      <Section title="Coming back" hint="Pickups from site: schedule → picked up → in transit. Stock rises when the dock checks it in above." testId="receiving-pickups">
        <OnsiteReviewPanel mode="pickups" actor={actor} installers={installers} />
      </Section>
    </div>
  );
}
